import { Link } from "react-router-dom";

const LAST_UPDATED = "29 April 2026";

export function Terms() {
  return (
    <section className="mx-auto max-w-3xl px-6 py-12">
      <h1 className="font-serif text-4xl tracking-tight">Terms of Service</h1>
      <p className="mt-3 text-sm text-neutral-500">Last updated: {LAST_UPDATED}</p>

      <div className="mt-8 space-y-8 text-neutral-700 leading-relaxed">
        <p>
          These Terms of Service ("Terms") govern your use of back2life.ai (the
          "Service"). By creating an account or using the Service you agree to
          these Terms. If you don't agree, please don't use the Service.
        </p>

        <Section title="1. The service">
          <p>
            back2life.ai lets you upload a photograph and receive a short
            AI-generated video story based on that photograph. The Service
            relies on third-party AI providers and their output is inherently
            unpredictable — we cannot guarantee any specific result, style or
            likeness.
          </p>
        </Section>

        <Section title="2. Eligibility and accounts">
          <ul className="list-disc pl-5 space-y-2">
            <li>You must be at least 16 years old to use the Service.</li>
            <li>You must provide a valid email address and keep it current.</li>
            <li>You are responsible for keeping your password confidential and for all activity on your account.</li>
            <li>One person, one account. Sharing accounts is not allowed.</li>
            <li>We may suspend or terminate accounts that violate these Terms.</li>
          </ul>
        </Section>

        <Section title="3. Photos you upload">
          <p>
            <strong>You retain full ownership</strong> of any photo you upload.
            You grant back2life.ai a limited, non-exclusive, royalty-free
            licence to store, process and transmit your photo to our AI
            sub-processors solely to deliver the Service to you. We do not use
            your photos to train AI models, and we do not share them with third
            parties for any other purpose.
          </p>
          <p className="mt-3">By uploading a photo you confirm that:</p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>You own the photo or have permission from the copyright holder.</li>
            <li>Every identifiable person in the photo has consented to it being animated, or you have legal authority to act on their behalf (e.g. a deceased family member).</li>
            <li>The photo does not contain anyone you know to be under 18 in a sexualised, distressed or violent context.</li>
          </ul>
        </Section>

        <Section title="4. Generated videos">
          <p>
            Subject to your payment, you receive a worldwide, royalty-free
            licence to use the generated video for personal and commercial
            purposes, including printing, sharing on social media, gifting and
            commercial promotion. Because AI output cannot itself be
            copyrighted in many jurisdictions, you should not assume exclusive
            rights over it.
          </p>
          <p className="mt-3">
            You agree <strong>not</strong> to use generated videos to:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>Mislead others about the actions, statements or beliefs of a real person.</li>
            <li>Defame, harass, intimidate or threaten any individual.</li>
            <li>Generate political disinformation or election-related deepfakes.</li>
            <li>Impersonate public figures, celebrities or government officials.</li>
            <li>Create content that violates the law of any jurisdiction in which you, or the person depicted, reside.</li>
          </ul>
        </Section>

        <Section title="5. Acceptable use">
          <p>You may not use the Service to upload or generate:</p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>Nudity, sexual content, or any sexual content depicting minors.</li>
            <li>Graphic violence, gore, or content glorifying self-harm or terrorism.</li>
            <li>Content that infringes the intellectual property or privacy rights of others.</li>
            <li>Hateful content targeting people based on race, religion, gender, sexuality or other protected characteristics.</li>
            <li>Malware, phishing material, or anything intended to harm our Service or other users.</li>
          </ul>
          <p className="mt-3">
            We use automated content moderation and may also review uploads
            manually if abuse is suspected. Violations may result in immediate
            termination without refund.
          </p>
        </Section>

        <Section title="6. Pricing and payment">
          <p>
            Pricing is shown in the checkout flow and is processed securely by
            Stripe. Prices are in US dollars unless stated otherwise. You
            authorise us (via Stripe) to charge the payment method you provide
            for the purchase you confirm.
          </p>
          <p className="mt-3">
            Each paid generation includes up to 2 free variations of the same
            scene, available while your source photo is still on file (7 days).
          </p>
        </Section>

        <Section title="7. Refunds">
          <p>
            Each video generation consumes real GPU compute that we cannot
            recover. As a rule, payments are non-refundable once a generation
            has started. We will, however, offer a refund or a regeneration at
            our discretion if:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>The Service failed to produce any video at all due to a bug on our side.</li>
            <li>The output is materially broken (e.g. a black frame, unrelated content).</li>
            <li>You were charged twice for the same job.</li>
          </ul>
          <p className="mt-3">
            To request a refund, email{" "}
            <a className="underline" href="mailto:contact@craft-codex.com">
              contact@craft-codex.com
            </a>{" "}
            within 14 days of the charge. Statutory rights under the Australian
            Consumer Law are not affected.
          </p>
        </Section>

        <Section title="8. AI output disclaimer">
          <p>
            AI-generated videos may contain unexpected artefacts, distortions,
            inaccurate likenesses, or content you find unflattering. The
            Service is provided "as is". We do not promise that any specific
            video will meet your expectations. If you're unhappy with the
            result, you can use one of your free variations or contact support.
          </p>
        </Section>

        <Section title="9. Termination">
          <p>
            You may stop using the Service and delete your account at any time
            via "My videos" or by contacting us. We may suspend or terminate
            your access if you breach these Terms, if your account poses a
            security or legal risk, or if we discontinue the Service.
          </p>
          <p className="mt-3">
            Sections that by their nature should survive termination
            (intellectual property, disclaimers, liability limits, governing
            law) will continue to apply.
          </p>
        </Section>

        <Section title="10. Limitation of liability">
          <p>
            To the maximum extent permitted by law, back2life.ai will not be
            liable for any indirect, incidental, special, consequential or
            punitive damages, or for any loss of profits, revenue, data or
            goodwill, arising out of or in connection with your use of the
            Service. Our total aggregate liability for any claim relating to
            the Service is limited to the amount you paid us in the 12 months
            preceding the claim, or AUD 100, whichever is greater.
          </p>
          <p className="mt-3">
            Nothing in these Terms excludes any non-excludable consumer
            guarantee, warranty or right under the Australian Consumer Law.
          </p>
        </Section>

        <Section title="11. Indemnity">
          <p>
            You agree to indemnify and hold us harmless against any claim
            brought by a third party arising from (a) photos you upload that
            you did not have the right to use, (b) your misuse of generated
            videos, or (c) your breach of these Terms.
          </p>
        </Section>

        <Section title="12. Changes to the service or these terms">
          <p>
            We may update the Service and these Terms from time to time. For
            material changes we will notify you by email or via a notice on
            the site at least 14 days before the change takes effect. Your
            continued use of the Service after the effective date constitutes
            acceptance of the new Terms.
          </p>
        </Section>

        <Section title="13. Governing law">
          <p>
            These Terms are governed by the laws of New South Wales,
            Australia. You and back2life.ai submit to the exclusive
            jurisdiction of the courts of New South Wales for any dispute,
            except where mandatory consumer-protection law in your country of
            residence requires otherwise.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>
            Questions about these Terms? Email{" "}
            <a className="underline" href="mailto:contact@craft-codex.com">
              contact@craft-codex.com
            </a>{" "}
            or use our{" "}
            <Link to="/contact" className="underline">
              contact form
            </Link>
            .
          </p>
        </Section>
      </div>
    </section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-serif text-2xl tracking-tight text-neutral-900">{title}</h2>
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );
}
